import { ChartOfAccountsTemplateVariableKind } from "../../../constants/domains/accounting/chartOfAccountsTemplateAccount";
import { AccountType } from "../../../constants/domains/accounting/accounts";
import { Form1099Box } from "@common/constants/domains/accounting/form1099Box";
import { AccountRole } from "./account";

// Per-account outcome of applying a CoA template to an entity. Consumed by the
// preview endpoint (BE-3, surfaced to the frontend) and the apply endpoint
// (BE-4). Kept as a shared enum so the frontend can render the "New" badge and
// status filtering without a backend-only import.
export enum AccountMergeStatus {
    new = "new",
    updated = "updated",
    unchanged = "unchanged",
    // Ordering-only change: the account's position within its scope changed
    // but nothing else did. Distinct from `updated` so a first apply after the
    // code-anchored backfill doesn't badge (or count) the whole CoA as updated.
    moved = "moved",
}

// Where a preview account or group comes from, relative to the template <>
// entity diff. Drives the preview's "Origin" filter (Matched / Entity-only /
// Template-only) and the Origin column on account and group rows.
export enum PreviewAccountOrigin {
    // On the entity AND matched to a template account (updated or left as-is).
    matched = "matched",
    // On the entity, with no matching template account — untouched by apply.
    entityOnly = "entityOnly",
    // In the template, not yet on the entity — the apply would create it.
    templateOnly = "templateOnly",
}

// A material change the apply would make to a preview row, surfaced as a tag +
// tooltip and the "Updates" filter. Only changes the engine actually performs
// today are modelled; renumbered is intentionally deferred, and a role-paired
// holder's rename shows only as its new name.
export enum PreviewAccountChange {
    // A template-only account/group the apply would add.
    added = "added",
    // A matched account whose group the apply would change to match the template.
    reassigned = "reassigned",
    // A matched account this apply transitions from active to archived.
    archived = "archived",
}

// Plain (TypeORM-decoupled) inputs the merge engine needs. Only the fields the
// algorithm actually reads are modelled, so callers can map from entities or
// from a preview payload without depending on the engine internals.

export interface TemplateApplyAccountInput {
    id: string;
    code: number | null;
    codeText: string | null;
    kickOriginalCode: number | null;
    name: string;
    type: AccountType;
    isDisabled: boolean;
    isManuallyCreated: boolean;
    role: AccountRole | null;
    // Variable rows (variableKind !== null) are ignored by the engine; later
    // PRs handle them. Kept on the input so callers can pass the full set.
    variableKind: ChartOfAccountsTemplateVariableKind | null;
    form1099Box: Form1099Box | null;
    parentGroupId: string | null;
    sortOrder: number;
}

export interface TemplateApplyGroupInput {
    id: string;
    name: string;
    type: AccountType;
    parentGroupId: string | null;
}

export interface EntityApplyAccountInput {
    id: string;
    code: number;
    codeText: string | null;
    kickOriginalCode: number | null;
    name: string;
    type: AccountType;
    isDisabled: boolean;
    isManuallyCreated: boolean;
    parentGroupId: string | null;
    sourceTemplateAccountId: string | null;
    sortOrder: number;
}

export interface EntityApplyGroupInput {
    id: string;
    name: string;
    type: AccountType;
    parentGroupId: string | null;
}

// A resolved pointer to the live workspace group a template group maps onto:
// either an existing workspace group, or a new group BE-4 must create (keyed by
// a deterministic temp id so the plan is reproducible across preview + apply).
export type ResolvedGroupReference =
    | { kind: "existing"; groupId: string }
    | { kind: "new"; tempId: string };

// An entity group the apply step must create. Captured parent-first (see
// TemplateApplyGroupResolutions.newGroups) so BE-4 can create parents before
// children. `parent` references either an existing entity group, another new
// group (by tempId), or null for a top-level group.
export interface NewGroupPlan {
    tempId: string;
    name: string;
    type: AccountType;
    parent: ResolvedGroupReference | null;
}

export interface TemplateApplyGroupResolutions {
    // Only contains template groups referenced by a processed (non-variable)
    // account and their ancestors.
    byTemplateGroupId: Map<string, ResolvedGroupReference>;
    // Ordered parent-first; create in this order to satisfy the parentGroupId FK.
    newGroups: NewGroupPlan[];
}

// NOTE: the engine intentionally does NOT compute `subtype`/`class`. Template
// accounts carry neither, and BE-4 derives them via the existing account
// creation services. `type` is included so a consumer (e.g. the BE-3 preview)
// can derive `class` itself via `getAccountClassForType` when needed.
export interface TemplateApplyAccountCreate {
    // Null when the plan came from a non-template source (copy-from-entity),
    // whose synthetic ids have no template_account row. The apply path always
    // sets a real id, so this is behaviour-neutral for apply.
    sourceTemplateAccountId: string | null;
    code: number;
    codeText: string | null;
    kickOriginalCode: number | null;
    name: string;
    type: AccountType;
    isDisabled: boolean;
    isManuallyCreated: boolean;
    form1099Box: Form1099Box | null;
    targetGroup: ResolvedGroupReference | null;
    mergeStatus: AccountMergeStatus.new;
    // Final slot index within the account's destination (type, parent) scope.
    sortOrder: number;
}

// A sync of an existing entity account. The merge rules never change an
// account's `type` or `code`: `type` is omitted entirely, while `code` echoes
// the entity's CURRENT value. `name` echoes it too, except for a role holder
// paired by role, which takes the template row's name.
export interface TemplateApplyAccountUpdate {
    entityAccountId: string;
    // Null for a non-template source; see TemplateApplyAccountCreate.sourceTemplateAccountId.
    sourceTemplateAccountId: string | null;
    code: number;
    name: string;
    isDisabled: boolean;
    targetGroup: ResolvedGroupReference | null;
    mergeStatus:
        | AccountMergeStatus.updated
        | AccountMergeStatus.unchanged
        | AccountMergeStatus.moved;
    // Present only when the account's stored sortOrder differs from its final
    // slot. A group/isDisabled sync stays `updated`; a matched account whose
    // position within its scope changed (and nothing else) becomes `moved`; a
    // pure renumbering (same relative position, new slot value) and entity-only
    // slot-keeping entries stay `unchanged` (write the slot, show no badge).
    sortOrder?: number;
}

export interface TemplateApplyPlan {
    groupResolutions: TemplateApplyGroupResolutions;
    accountsToCreate: TemplateApplyAccountCreate[];
    accountsToUpdate: TemplateApplyAccountUpdate[];
    // Every entity account a template account matched, including a retyped Kick
    // account the engine leaves untouched, so the preview can label
    // origin as `matched` vs `entityOnly`. Contains no created/new-account ids.
    matchedEntityAccountIds: string[];
    templateRoles: TemplateApplyRoles;
}

export interface TemplateApplyRolePairing {
    // Role holders without journal entry lines, the only ones a role row may pair with.
    pairableHolderIdByRole: Map<AccountRole, string>;
}

export interface TemplateApplyRoles {
    // Every matched entity account, including an ignored retyped Kick account, with its row's role.
    roleByMatchedEntityAccountId: Map<string, AccountRole | null>;
    roleByCreatedTemplateAccountId: Map<string, AccountRole>;
}
